<template>
  <q-page class="home" :style-fn="homeStyleFn">
    <q-drawer
      v-model="showFilter"
      side="left"
      :width="300"
      overlay
      behavior="mobile"
    >
      <Filters @close="toggleFilters" />
    </q-drawer>
    <template v-if="!currentUser">
      <Map
        @toggle-filters="toggleFilters"
        @maps-api-loaded="onMapsApiLoaded"
      ></Map>
    </template>
    <template v-else-if="isPremium && validSubscriptionTerm">
      <q-splitter v-model="splitterModel" horizontal style="height: 100%">
        <template v-slot:before>
          <Map
            @toggle-filters="toggleFilters"
            @marker-hover="handleMarkerHover"
            @maps-api-loaded="onMapsApiLoaded"
          ></Map>
        </template>
        <template v-slot:separator>
          <q-avatar
            color="primary"
            text-color="white"
            size="40px"
            icon="drag_indicator"
          />
        </template>
        <template v-slot:after>
          <q-table
            class="filtered-courses__table"
            ref="coursesTable"
            :columns="listViewColumnsConfig"
            :visible-columns="filterColumns"
            :data="filteredCourses"
            :hide-pagination="true"
            :pagination="initialPagination"
            :sort-method="customSort"
            :filter="listViewTableFilter"
            binary-state-sort
            bordered
            dense
            flat
            no-data-label="There are no courses matching the filter criteria."
            row-key="id"
            separator="cell"
            square
            virtual-scroll
            :virtual-scroll-slice-size="250"
            :virtual-scroll-slice-ratio-before="3"
            :virtual-scroll-slice-ratio-after="3"
            :virtual-scroll-sticky-size-start="28"
            :virtual-scroll-item-size="28"
            :table-colspan="filterColumns.length"
          >
            <template v-slot:top>
              <q-input
                class="gt-xs"
                clearable
                dense
                outlined
                debounce="300"
                color="primary"
                v-model="listViewTableFilter"
                placeholder="Filter..."
              >
              </q-input>
              <q-input
                class="lt-sm"
                clearable
                dense
                outlined
                debounce="300"
                color="primary"
                v-model="listViewTableFilter"
                placeholder="Filter..."
                style="width: 80px"
              >
              </q-input>
              <q-toggle
                :dense="denseListView"
                :value="smartZoom"
                @input="toggleSmartZoom"
                size="sm"
              >
                <q-item-label>Smart Zoom</q-item-label>
                <q-item-label caption class="gt-xs"
                  >Filters courses within current viewable map
                  area</q-item-label
                >
              </q-toggle>
              <q-space />
              <q-btn
                flat
                icon="view_column"
                color="primary"
                size="14px"
                title="Customize Columns"
                @click="columnSelectionDialog = true"
              />
              <q-btn
                flat
                icon="info"
                color="primary"
                size="14px"
                title="Column Descriptions"
                @click="columnDescriptionDialog = true"
              />
            </template>
            <template v-slot:header="props">
              <q-tr :props="props">
                <q-th
                  v-for="col in props.cols"
                  :key="col.name"
                  :props="props"
                  :title="col.description"
                  auto-width
                >
                  {{ col.label }}
                </q-th>
              </q-tr>
            </template>
            <template v-slot:body="props">
              <q-tr
                :props="props"
                v-bind:class="{
                  'course--closed-permanently': isNotOperating(
                    props.row.OLGDP011,
                  ),
                  'course--is-in-wishlist': isInWishlist(props.row.id),
                  'course--is-in-courses-played': coursePlayed(props.row.id),
                  'course--is-hovered': isHovering(props.row.id),
                  'course--is-selected': isClicked(props.row.id),
                }"
                @mouseenter="handleMouseEnter(props.row.id)"
                @mouseleave="handleMouseLeave(props.row.id)"
              >
                <q-td
                  v-for="col in props.cols"
                  :key="col.name"
                  :props="props"
                  @click="handleRowClick(props.key)"
                >
                  <span v-if="col.value === PREMIUM_DATA_MASK">
                    <span title="Please login or register for an account."
                      ><q-icon name="lock"
                    /></span>
                  </span>
                  <span v-else-if="col.name === 'OLGDP010'">
                    <a
                      :href="col.value"
                      :title="col.value"
                      target="_blank"
                      rel="noopener noreferrer"
                      >Visit Website</a
                    >
                  </span>
                  <span v-else>{{ col.value }}</span>
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </template>
      </q-splitter>
      <q-dialog v-model="columnDescriptionDialog">
        <q-layout container class="bg-white">
          <q-header class="bg-primary">
            <q-toolbar>
              <q-toolbar-title>Column Descriptions</q-toolbar-title>
              <q-btn
                flat
                round
                dense
                icon="close"
                @click="columnDescriptionDialog = false"
              />
            </q-toolbar>
          </q-header>

          <q-page-container>
            <q-page padding>
              <dl v-for="column in columnDescriptions" :key="column.Name">
                <dt>
                  {{ column.Name }}
                </dt>
                <dd>
                  {{ column.Description }}
                </dd>
              </dl>
            </q-page>
          </q-page-container>
        </q-layout>
      </q-dialog>
      <q-dialog v-model="columnSelectionDialog">
        <q-layout container class="bg-white">
          <q-header class="bg-primary">
            <q-toolbar>
              <q-toolbar-title>Customize Columns</q-toolbar-title>
              <q-btn
                flat
                round
                dense
                icon="close"
                @click="columnSelectionDialog = false"
              />
            </q-toolbar>
          </q-header>
          <q-page-container>
            <q-page padding>
              <ListViewColumnsManager />
            </q-page>
          </q-page-container>
        </q-layout>
      </q-dialog>
    </template>
  </q-page>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Map from "@/components/Map.vue";
import Filters from "@/components/Filters.vue";
import ListViewColumnsManager from "@/components/ListViewColumnsManager.vue";
import { PREMIUM_DATA_MASK } from "@/constants";

export default {
  name: "Home",
  components: {
    Filters,
    ListViewColumnsManager,
    Map,
  },
  data() {
    return {
      showFilter: false,
      selectedTab: "map",
      selectedCourses: [],
      columnDescriptionDialog: false,
      columnSelectionDialog: false,
      listViewTableFilter: "",
      splitterModel: 60,
      homeStyleOffset: null,
      seamless: false,
      rowMouseEnterTimer: null,
      rowMouseLeaveTimer: null,
      initialPagination: {
        sortBy: "OLGDP001",
        descending: false,
        page: 0,
        rowsPerPage: 0,
      },
      mapsApiLoaded: false,
    };
  },
  beforeCreate() {
    this.PREMIUM_DATA_MASK = PREMIUM_DATA_MASK;
  },
  mounted() {},
  computed: {
    columnDescriptions() {
      return this.listViewColumnsConfig.map(m => {
        return {
          Name: m.label,
          Description: m.description,
        };
      });
    },
    ...mapState([
      "currentUser",
      "isLoading",
      "courseMetadata",
      "filterInput",
      "filterColumns",
      "hoveredCourseId",
      "clickedCourseId",
      "denseListView",
      "validSubscriptionTerm",
      "smartZoom",
    ]),
    ...mapGetters([
      "isPremium",
      "filteredCourses",
      "listViewColumnsConfig",
      "listViewNumericColumns",
      "listViewDateColumns",
      "wishlistCourseIds",
      "coursePlayedCourseIds",
      "listViewColumnsGroupedByCategory",
      "listViewColumnsCategories",
    ]),
    toggleFilterLabel() {
      return this.showFilter ? "Hide" : "Show";
    },
    mapsFilteredCourses() {
      return this.selectedTab === "map" ? this.filteredCourses : [];
    },
    homeStyleMinHeight() {
      const offset = this.homeStyleOffset;
      // minHeight is actually what the default style-fn returns in Quasar
      const minHeight = offset ? `calc(100vh - ${offset}px)` : "100vh";
      return minHeight;
    },
    homeStyleHeight() {
      const height = this.homeStyleMinHeight;
      return height;
    },
    homeStyle() {
      return {
        // we override to fixed height instead of min-height
        height: this.homeStyleHeight,
      };
    },
  },
  methods: {
    ...mapActions(["updateWishlist", "updateCoursePlayed"]),
    onMapsApiLoaded() {
      this.mapsApiLoaded = true;
    },
    customSort(rows, sortBy, descending) {
      const data = [...rows];

      if (sortBy) {
        data.sort((a, b) => {
          const x = descending ? b : a;
          const y = descending ? a : b;

          // in descending order, if x is empty or null, a is less than b
          if (x[sortBy] === "" || x[sortBy] == null) return descending ? -1 : 1;

          // in descending order, if y is empty or null, a is greater than b
          if (y[sortBy] === "" || y[sortBy] == null) return descending ? 1 : -1;

          // if both values are Next 50, they are equal
          if (x[sortBy] === "Next 50" && y[sortBy] === "Next 50") return 0;

          // in descending order, if x is "Next 50", a is less than b
          if (x[sortBy] === "Next 50") return descending ? -1 : 1;

          // in descending order, if y is "Next 50", a is greater than b
          if (y[sortBy] === "Next 50") return descending ? 1 : -1;

          const isNumericSort = this.listViewNumericColumns.indexOf(sortBy) > 0;
          const isDateSort = this.listViewDateColumns.indexOf(sortBy) > 0;

          if (isNumericSort) {
            // maxFee, powerCartFee
            // we should sort these by the converted value, not the raw value as it's in multiple currencies
            if (["OLGDP012", "OLGDP098"].includes(sortBy)) {
              return x[`${sortBy}_converted`] - y[`${sortBy}_converted`];
            }

            return x[sortBy] - y[sortBy];
          } else if (isDateSort) {
            return y[sortBy] - x[sortBy];
          } else {
            // string sort
            return x[sortBy] > y[sortBy] ? 1 : x[sortBy] < y[sortBy] ? -1 : 0;
          }
        });
      }

      return data;
    },
    toggleFilters() {
      this.showFilter = !this.showFilter;
    },
    saveSelectedToWishList() {
      console.debug("saveSelectedToWishList", this.selectedCourses);
    },
    isInWishlist(id) {
      return this.wishlistCourseIds.includes(id);
    },
    toggleInWishlist(id) {
      this.updateWishlist(id);
    },
    saveSelectedToCoursePlayed() {
      console.debug("saveSelectedToCoursePlayed", this.selectedCourses);
    },
    coursePlayed(id) {
      return this.coursePlayedCourseIds.includes(id);
    },
    toggleCoursePlayed(id) {
      this.updateCoursePlayed(id);
    },
    isNotOperating(operatingStatus) {
      return operatingStatus === "Closed - Permanently";
    },
    handleMouseEnter(id) {
      clearTimeout(this.rowMouseLeaveTimer);

      this.rowMouseEnterTimer = setTimeout(() => {
        // console.debug("over", id);
        this.setHoveredCourseId(id);
      }, 1050);
    },
    setHoveredCourseId(id) {
      this.$store.commit("setHoveredCourseId", id);
    },
    handleMouseLeave() {
      clearTimeout(this.rowMouseEnterTimer);

      this.rowMouseLeaveTimer = setTimeout(() => {
        // console.debug("out", id);
        this.resetHoveredCourseId();
      }, 100);
    },
    resetHoveredCourseId() {
      if (this.hoveredCourseId === "") {
        return;
      }

      this.$store.commit("setHoveredCourseId", "");
    },
    isHovering(id) {
      return this.hoveredCourseId === id;
    },
    handleRowClick(id) {
      this.setClickedCourseId(id);
      // this.resetHoveredCourseId();
    },
    setClickedCourseId(id) {
      this.$store.commit("setClickedCourseId", id);
    },
    resetClickedCourseId() {
      this.$store.commit("setClickedCourseId", "");
    },
    isClicked(id) {
      return this.clickedCourseId === id;
    },
    homeStyleFn(offset) {
      this.homeStyleOffset = offset;

      return this.homeStyle;
    },
    handleMarkerHover(courseId) {
      const coursesTableRef = this.$refs.coursesTable;

      if (!coursesTableRef) {
        return;
      }

      const rows = coursesTableRef.computedRows;
      const courseIndex = rows.findIndex(c => c.id === courseId);

      if (courseIndex >= 0) {
        coursesTableRef.scrollTo(courseIndex);
      }
    },
    toggleSmartZoom() {
      this.$store.commit("setSmartZoom", !this.smartZoom);

      if (!this.$refs.coursesTable) {
        return;
      }

      this.$refs.coursesTable.$el.querySelector(
        ".q-table__middle.scroll",
      ).scrollTop = 0;
    },
  },
};
</script>
<style lang="scss" scoped>
.container {
  height: calc(100vh - 54px);
}
</style>
<style lang="scss">
// not using scoped because it wasn't working with q-table
// needs further investigation
.filtered-courses__table {
  width: 100%;
  height: 100%;
}
.filtered-courses__table .q-table__top,
.filtered-courses__table .q-table__bottom,
.filtered-courses__table thead tr:first-child th {
  background-color: #fff;
}
.filtered-courses__table thead tr th {
  background: #fff;
  font-weight: bold;
  position: sticky;
  z-index: 2;
}
.filtered-courses__table thead tr:first-child th {
  border-bottom-width: 2px;
  top: 0;
  // z-index: 1;
}
.filtered-courses__table thead tr:first-child th:first-child {
  border-right-width: 2px;
  z-index: 3;
}

.filtered-courses__table thead tr th:first-child {
  position: sticky;
  left: 0;
}

// .filtered-courses__table thead th,
// .filtered-courses__table tbody td {
//   white-space: normal;
//   overflow-wrap: break-word;
// }

.filtered-courses__table tbody.q-virtual-scroll__content td:first-child {
  background: #fff;
  border-right-width: 2px;
  font-weight: bold;
  position: sticky;
  left: 0;
  z-index: 1;
}

.filtered-courses__table tbody.q-virtual-scroll__content tr {
  cursor: pointer;

  &.course--is-in-wishlist,
  &.course--is-in-wishlist td:first-child,
  &.course--is-in-wishlist td:nth-child(2) {
    background-color: #fff8e1;
  }

  &.course--is-in-courses-played,
  &.course--is-in-courses-played td:first-child,
  &.course--is-in-courses-played td:nth-child(2) {
    background-color: #f1f8e9;
  }

  &.course--closed-permanently,
  &.course--closed-permanently td:first-child,
  &.course--closed-permanently td:nth-child(2) {
    background-color: #ffebee;
    color: #d50000;
  }

  &.course--is-hovered,
  &.course--is-hovered td:first-child,
  &.course--is-hovered td:nth-child(2) {
    background-color: #e1f5fe;
  }

  &.course--is-selected,
  &.course--is-selected td:first-child,
  &.course--is-selected td:nth-child(2) {
    background-color: #e1f5fe;
  }
}

// .filtered-courses__table tr.course--closed-permanently,
// .filtered-courses__table tr.course--closed-permanently td:first-child,
// .filtered-courses__table tr.course--closed-permanently td:nth-child(2) {
//   background-color: #ffebee;
//   color: #d50000;
// }

.filtered-courses__table tbody.q-virtual-scroll__padding td:first-child {
  background-color: transparent;
}

@media (max-width: 599px) {
  .filtered-courses__table th.col--course-id,
  .filtered-courses__table td.col--course-id {
    min-width: 150px;
    white-space: normal;
  }
}
</style>
